import { useRef, RefObject, MutableRefObject } from "react";
import {
  calculateConstrainedPan,
  getHorizontalPanEdge,
} from "../utils/zoomMath";
import { ZoomPointerEvent } from "../types/zoom";

const DEFAULT_MINIMUM_DRAG_DISTANCE = 2;
const SWIPE_THRESHOLD_PX = 10;
const MAX_VERTICAL_DRIFT_PX = 30;
const PERCENTAGE_SCALE = 100;

interface UseDragPanParams {
  containerRef: RefObject<HTMLElement>;
  isEnabled: boolean;
  currentScaleRef: MutableRefObject<number>;
  positionXRef: MutableRefObject<number>;
  positionYRef: MutableRefObject<number>;
  contentWidth: number;
  contentHeight: number;
  onPan: (x: number, y: number) => void;
  hasDraggedRef: MutableRefObject<boolean>;
  minimumDragDistance?: number;
  onZoomedEdgeSwipeNavigate?: (direction: "previous" | "next") => void;
}

interface UseDragPanReturn {
  onPointerDown: (event: ZoomPointerEvent) => void;
  onPointerMove: (event: ZoomPointerEvent) => void;
  onPointerUp: (event: ZoomPointerEvent) => void;
}

export default function useDragPan({
  containerRef,
  isEnabled,
  currentScaleRef,
  positionXRef,
  positionYRef,
  contentWidth,
  contentHeight,
  onPan,
  hasDraggedRef,
  minimumDragDistance = DEFAULT_MINIMUM_DRAG_DISTANCE,
  onZoomedEdgeSwipeNavigate,
}: UseDragPanParams): UseDragPanReturn {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startEdge: ReturnType<typeof getHorizontalPanEdge>;
    lastX: number;
    lastY: number;
  } | null>(null);

  const onPointerDown = (event: ZoomPointerEvent): void => {
    if (!isEnabled) {
      return;
    }
    const { width: containerWidth } =
      containerRef.current.getBoundingClientRect();

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startEdge: getHorizontalPanEdge({
        containerWidth,
        contentWidth,
        scale: currentScaleRef.current,
        positionX: positionXRef.current,
      }),
      lastX: event.clientX,
      lastY: event.clientY,
    };
    hasDraggedRef.current = false;

    containerRef.current.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ZoomPointerEvent): void => {
    if (!dragRef.current || !isEnabled) {
      return;
    }

    const dragDistanceX = event.clientX - dragRef.current.lastX;
    const dragDistanceY = event.clientY - dragRef.current.lastY;

    const belowMinimumDragDistance =
      !hasDraggedRef.current &&
      Math.abs(dragDistanceX) < minimumDragDistance &&
      Math.abs(dragDistanceY) < minimumDragDistance;

    if (belowMinimumDragDistance) {
      return;
    }

    hasDraggedRef.current = true;

    const scale = currentScaleRef.current;
    const { width: containerWidth, height: containerHeight } =
      containerRef.current.getBoundingClientRect();

    const panDeltaX =
      (-dragDistanceX * PERCENTAGE_SCALE) / (scale * contentWidth);
    const panDeltaY =
      (-dragDistanceY * PERCENTAGE_SCALE) / (scale * contentHeight);

    const { x: newX, y: newY } = calculateConstrainedPan({
      containerWidth,
      containerHeight,
      contentWidth,
      contentHeight,
      scale,
      currentX: positionXRef.current,
      currentY: positionYRef.current,
      deltaX: panDeltaX,
      deltaY: panDeltaY,
    });

    onPan(newX, newY);
    dragRef.current = {
      ...dragRef.current,
      lastX: event.clientX,
      lastY: event.clientY,
    };
  };

  const onPointerUp = (event: ZoomPointerEvent): void => {
    try {
      containerRef.current.releasePointerCapture(event.pointerId);
    } catch {
      /* pointer capture may already be released */
    }

    if (dragRef.current?.startEdge) {
      const { startX, startY, startEdge } = dragRef.current;
      const dragDistanceX = event.clientX - startX;
      const dragDistanceY = event.clientY - startY;

      const { width: containerWidth } =
        containerRef.current.getBoundingClientRect();

      const panEndEdge = getHorizontalPanEdge({
        containerWidth,
        contentWidth,
        scale: currentScaleRef.current,
        positionX: positionXRef.current,
      });

      const isSwipeAtSameEdge =
        panEndEdge === startEdge &&
        Math.abs(dragDistanceY) <= MAX_VERTICAL_DRIFT_PX;

      if (isSwipeAtSameEdge) {
        if (startEdge === "left" && dragDistanceX >= SWIPE_THRESHOLD_PX) {
          onZoomedEdgeSwipeNavigate?.("previous");
        } else if (
          startEdge === "right" &&
          dragDistanceX <= -SWIPE_THRESHOLD_PX
        ) {
          onZoomedEdgeSwipeNavigate?.("next");
        }
      }
    }

    dragRef.current = null;
  };

  return { onPointerDown, onPointerMove, onPointerUp };
}
